import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip } from 'recharts';
import { DarkCard } from './DashboardStyles';

const AgePyramidWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CategoriesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  padding: 0 10px;
`;

const CategoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #c0c0c0;
  transition: all 0.3s ease;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.color};
    flex-shrink: 0;
  }
  .pct {
    color: #ffffff;
    font-weight: 600;
    margin-left: 2px;
  }
`;

const ChartContainer = styled.div`
  cursor: pointer;
  user-select: none;
  .recharts-bar-rectangle {
    transition: opacity 0.3s ease;
  }
`;

const ClickableAxisTick = (props) => {
    const { x, y, payload, selectedGroup, onSelect } = props;
    const isSelected = selectedGroup === payload.value;

    return (
        <g
            transform={`translate(${x},${y})`}
            onClick={(e) => {
                e.stopPropagation();
                onSelect(payload.value === selectedGroup ? null : payload.value);
            }}
            style={{ cursor: 'pointer' }}
        >
            <text
                x={-10}
                y={0}
                dy={4}
                textAnchor="end"
                fill={isSelected ? '#BA955E' : '#c0c0c0'}
                fontSize={11}
                fontWeight={isSelected ? 'bold' : 'normal'}
            >
                {payload.value}
            </text>
        </g>
    );
};

const UserDemographicsCard = ({ ageData = [], categoriesData = [] }) => {
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Calculate current percentages based on selection
    const displayPercentages = useMemo(() => {
        if (!ageData.length || !categoriesData.length) return {};

        const result = {};
        const categoryNames = categoriesData.map(c => c.name);

        if (selectedGroup) {
            const groupData = ageData.find(d => d.grupo === selectedGroup);
            if (groupData) {
                const total = categoryNames.reduce((acc, name) => acc + (groupData[name] || 0), 0);
                categoryNames.forEach(name => {
                    result[name] = total > 0 ? ((groupData[name] / total) * 100).toFixed(1) : 0;
                });
            }
        } else {
            // Calculate total for all 
            let grandTotal = 0;
            const totals = {};
            categoryNames.forEach(name => {
                const catSum = ageData.reduce((acc, d) => acc + (d[name] || 0), 0);
                totals[name] = catSum;
                grandTotal += catSum;
            });

            categoryNames.forEach(name => {
                result[name] = grandTotal > 0 ? ((totals[name] / grandTotal) * 100).toFixed(1) : 0;
            });
        }
        return result;
    }, [selectedGroup, ageData, categoriesData]);

    const handleChartClick = (data, event) => {
        if (!data || !data.activePayload) {
            setSelectedGroup(null);
            return;
        }
        const group = data.activeLabel;
        if (group) {
            setSelectedGroup(group === selectedGroup ? null : group);
        }
    };

    const handleCardClick = () => {
        setSelectedGroup(null);
    };

    return (
        <DarkCard onClick={handleCardClick} style={{ cursor: 'default' }}>
            <div className="card-title">Intereses por Rango de Edad</div>
            <AgePyramidWrapper>
                <ChartContainer onClick={(e) => e.stopPropagation()}>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart
                            layout="vertical"
                            data={ageData}
                            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                            stackOffset="expand"
                            barSize={18}
                            onClick={handleChartClick}
                        >
                            <XAxis type="number" hide />
                            <YAxis
                                dataKey="grupo"
                                type="category"
                                tick={<ClickableAxisTick selectedGroup={selectedGroup} onSelect={setSelectedGroup} />}
                                axisLine={false}
                                tickLine={false}
                                width={50}
                            />
                            {/* Recharts needs Tooltip for better hit detection */}
                            <Tooltip content={() => null} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                            {categoriesData.map((cat) => (
                                <Bar
                                    key={cat.name}
                                    dataKey={cat.name}
                                    stackId="a"
                                    fill={cat.color}
                                    isAnimationActive={true}
                                >
                                    {ageData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={cat.color}
                                            cursor="pointer"
                                            fillOpacity={selectedGroup === null || selectedGroup === entry.grupo ? 1 : 0.3}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedGroup(entry.grupo === selectedGroup ? null : entry.grupo);
                                            }}
                                        />
                                    ))}
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>

                <CategoriesList>
                    {categoriesData.map((cat, i) => (
                        <CategoryItem key={i} color={cat.color}>
                            <div className="dot" />
                            {cat.name}
                            <span className="pct">{displayPercentages[cat.name] || 0}%</span>
                        </CategoryItem>
                    ))}
                </CategoriesList>
            </AgePyramidWrapper>
        </DarkCard>
    );
};

export default UserDemographicsCard;
